import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { getPrimaryWebsiteUrl } from '@library/utilities/helpers/helper'
import { getUserData } from '@store/user/selectors'
import { Button, message, Spin, Tag } from 'gokwik-ui-kit'
import { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'

export default function WebsiteCheck({ allRequiredSectionsPresent, setAllRequiredSectionsPresent }) {
    const [loading, setLoading] = useState(false)
    const [sectionsData, setSectionsData] = useState({})
    const [errorMessage, setErrorMessage] = useState('')
    const [businessInfo, setBusinessInfo] = useState(null)
    const [issues, setIssues] = useState([])
    // const [allRequiredSectionsPresent, setAllRequiredSectionsPresent] = useState(false)
    const userData = useSelector(getUserData)
    const apiCallMadeRef = useRef(false)

    const requiredSections = ['about', 'contact', 'privacy', 'returns', 'terms', 'shipping']

    const policyItems = [
        { label: 'About us', key: 'about' },
        { label: 'Contact us', key: 'contact' },
        { label: 'Terms of services', key: 'terms' },
        { label: 'Privacy policy', key: 'privacy' },
    ]

    const returnPolicyItems = [
        { label: 'Refund process', key: 'returns' },
        { label: 'Cancellation process', key: 'returns' },
        { label: 'Refund and return', key: 'returns' },
    ]

    const shippingPolicyItems = [
        { label: 'Packaging details', key: 'shipping' },
        { label: 'Delivery timelines', key: 'shipping' },
        { label: 'Tracking details', key: 'shipping' },
    ]

    useEffect(() => {
        if (!apiCallMadeRef.current) {
            checkWebsiteReadiness()
            apiCallMadeRef.current = true
        }
    }, [])

    // Check if all required sections are present whenever sectionsData changes
    useEffect(() => {
        const areAllSectionsPresent = requiredSections.every((section) => sectionsData[section]?.present === true)
        setAllRequiredSectionsPresent(areAllSectionsPresent)
    }, [sectionsData])

    const checkWebsiteReadiness = async () => {
        const primaryWebsiteUrl = await getPrimaryWebsiteUrl(userData?.kycData?.website)

        if (!primaryWebsiteUrl) {
            message.error('Invalid Website URL! Please correct the URL is review section re-try', 3)
            return
        }
        setLoading(true)
        setErrorMessage('')

        try {
            const res = await makeAPICall({
                method: 'post',
                url: process.env.REACT_APP_BASE_URL + '/api/dashboard/onboarding/web-scrap',
                skipLoader: true,
                payload: {
                    url: primaryWebsiteUrl,
                    sections: requiredSections,
                },
            })

            if (res?.status === 200) {
                const responseData = res.data

                if (Object.keys(responseData?.data)?.length === 0) {
                    setErrorMessage(
                        'Facing some technical difficulties please click on the re-check button above and try again !',
                    )
                    return
                }

                if (responseData.error) {
                    setErrorMessage(responseData.error)
                } else if (responseData.data) {
                    const { sections, businessInfo, issues } = responseData.data
                    setSectionsData(sections || {})
                    setBusinessInfo(businessInfo || null)
                    setIssues(issues || [])
                } else {
                    setErrorMessage(
                        'Facing some technical difficulties please click on the re-check button and try again !',
                    )
                }
            } else {
                setErrorMessage(
                    'Facing some technical difficulties please click on the re-check button and try again !',
                )
            }
        } catch (error) {
            console.error('Error checking website readiness:', error)
            setErrorMessage('Failed to check website. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const isSectionAvailable = (key) => {
        return sectionsData[key]?.present === true
    }

    const getMissingPagesCount = () => {
        return requiredSections.filter((key) => !isSectionAvailable(key)).length
    }

    return (
        <div className='p-5 border rounded-xl shadow-sm bg-white'>
            <div className='flex justify-between items-center mb-4'>
                <h2 className='text-lg font-semibold'>Checking your website</h2>
                <Button
                    variant='primary'
                    onClick={() => {
                        apiCallMadeRef.current = true
                        checkWebsiteReadiness()
                    }}
                    disabled={loading}
                >
                    Re-check
                </Button>
            </div>

            <div className='bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4'>
                <p className='text-sm font-semibold text-yellow-800 mb-1'>
                    Required Details/Pages on your website for KYC Compliance
                </p>
                <p className='text-sm text-gray-700'>
                    As per RBI regulations, you're required to have the following details on your website.
                </p>
            </div>

            {errorMessage ? (
                <div className='bg-red-50 border border-red-200 rounded-md p-3 mb-4'>
                    <p className='text-sm text-red-700'>{errorMessage}</p>
                </div>
            ) : (
                !loading && (
                    <>
                        {allRequiredSectionsPresent ? (
                            <div className='bg-green-50 border border-green-200 rounded-md p-3 mb-4'>
                                <p className='text-sm text-green-700 font-semibold mb-1'>
                                    All required pages are available on your website
                                </p>
                            </div>
                        ) : (
                            getMissingPagesCount() > 0 && (
                                <div className='bg-red-50 border border-red-200 rounded-md p-3 mb-4'>
                                    <p className='text-sm text-red-700 font-semibold mb-1'>
                                        {getMissingPagesCount()} Pages are missing on your website
                                    </p>
                                    <p className='text-sm text-gray-700'>Complete your website within 5 days</p>
                                </div>
                            )
                        )}
                    </>
                )
            )}

            {issues && issues.length > 0 && (
                <>
                    <div className='bg-yellow-50 p-3 rounded-md text-red-500'>
                        <span className='text-xs pb-1'>Issues Found</span>
                        {issues.map((issue, index) => (
                            <div key={index} className='mb-3 last:mb-0'>
                                <p className='text-sm font-semibold mb-1'>{issue.type}</p>
                                <p className='text-xs text-gray-700 mb-1'>{issue.description}</p>
                                <p className='text-xs text-gray-900'>
                                    <span className='font-medium'>Recommendation:</span> {issue.recommendation}
                                </p>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <div>
                {policyItems.map((item) => (
                    <RowItem
                        key={item.label}
                        label={item.label}
                        loading={loading}
                        available={!loading && !errorMessage ? isSectionAvailable(item.key) : false}
                    />
                ))}

                <SectionHeading label='Return policy' />
                <div className='pl-4'>
                    {returnPolicyItems.map((item) => (
                        <RowItem
                            key={item.label}
                            label={item.label}
                            loading={loading}
                            available={!loading && !errorMessage ? isSectionAvailable(item.key) : false}
                        />
                    ))}
                </div>

                <SectionHeading label='Shipping policy' />
                <div className='pl-4'>
                    {shippingPolicyItems.map((item) => (
                        <RowItem
                            key={item.label}
                            label={item.label}
                            loading={loading}
                            available={!loading && !errorMessage ? isSectionAvailable(item.key) : false}
                        />
                    ))}
                </div>

                {businessInfo && (
                    <>
                        <SectionHeading label='Business Information' />
                        <div className='bg-gray-50 p-3 rounded-md'>
                            {businessInfo.businessName && (
                                <p className='text-sm mb-1'>
                                    <span className='font-medium'>Business Name:</span> {businessInfo.businessName}
                                </p>
                            )}
                            {businessInfo.address && (
                                <p className='text-sm mb-1'>
                                    <span className='font-medium'>Address:</span> {businessInfo.address}
                                </p>
                            )}
                            {businessInfo.contactInfo?.email && (
                                <p className='text-sm mb-1'>
                                    <span className='font-medium'>Email:</span> {businessInfo.contactInfo.email}
                                </p>
                            )}
                            {businessInfo.contactInfo?.phone && (
                                <p className='text-sm mb-1'>
                                    <span className='font-medium'>Phone:</span> {businessInfo.contactInfo.phone}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

function RowItem({ label, loading, available }) {
    return (
        <div className='flex justify-between items-center border-0 border-b border-solid border-gray-100 p-2'>
            <span className='text-xs text-gray-900'>{label}</span>
            {loading ? (
                <span className='flex items-center gap-1 text-xs text-gray-700'>
                    <Spin /> &nbsp;&nbsp; Checking
                </span>
            ) : (
                <Tag variant={available ? 'success' : 'error'}>{available ? 'Available' : 'Missing'}</Tag>
            )}
        </div>
    )
}

function SectionHeading({ label }: { label: string }) {
    return <div className='pt-4 pb-2 text-xs text-gray-800 font-medium'>{label}</div>
}
